<template>
  <div class="relative">
    <Menu v-slot="{ open }">
      <MenuButton>
        <div
          class="flex cursor-pointer items-center"
          :class="hideLabel ? '' : 'gap-2'"
        >
          <LanguageIcon class="h-7 w-7 shrink-0 stroke-2" />
          <span v-if="!hideLabel" class="truncate">{{ selected?.label }}</span>
          <ChevronDownIcon
            class="shrink-0 stroke-2 transition-transform"
            :class="[{ 'rotate-180': open }, hideLabel ? 'h-5 w-5' : 'h-6 w-6']"
          />

          <IntlMsg v-bind="{ k: 'a11y.select-language' }" class="sr-only" />
        </div>
      </MenuButton>

      <MenuItems
        class="absolute left-1/2 top-full z-10 mt-2 grid min-w-32 -translate-x-1/2 gap-1 rounded-xl bg-white p-2 shadow-xl focus:outline-none"
      >
        <MenuItem
          v-for="item in LOCALE_OPTIONS"
          :key="`locale-opt-${item.value}`"
          v-slot="{ active }"
        >
          <button
            class="c-btn block w-full rounded-md text-left text-lg lg:px-3 lg:py-1.5 lg:text-base"
            :class="{
              'bg-gray-200 text-gray-600': item.value === locale,
              'hover:bg-gray-100': item.value !== locale,
              'bg-blue-100': active,
            }"
            @click="changeLang(item.value)"
          >
            {{ item.label }}
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ChevronDownIcon, LanguageIcon } from '@heroicons/vue/24/outline'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'

import { IntlMsg } from '/@shared/app'

import { useAuthStore } from '/@/stores'
import {
  type AppLocale,
  LOCALE_OPTIONS,
  updateServerLocale,
  useI18n,
  selectLanguage,
} from '/@/i18n'

defineProps<{
  hideLabel?: boolean
}>()

const authStore = useAuthStore()

const route = useRoute()
const router = useRouter()

const { locale } = useI18n()

const selected = computed(() =>
  LOCALE_OPTIONS.find(x => x.value === locale.value)
)

async function changeLang(newLang: AppLocale) {
  if (newLang === locale.value) return

  const hasLangParams = 'lang' in route.params
  await updateServerLocale(newLang)

  if (authStore.user && !authStore.userNavItems[newLang])
    authStore.getUserContext()

  if (hasLangParams) {
    router.push({
      params: hasLangParams
        ? { lang: newLang !== 'en' ? newLang : '' }
        : undefined,
    })
  } else await selectLanguage(newLang)
}
</script>
